import { ButtonType, ButtonWidget, RadioButtonWithLabelGroupWidget, RadioLayoutType, TextFieldWidget } from '@tableau/widgets';
import React from 'react';

import { AuthenticationTypeResolutionService } from '~src/authentication/services/AuthenticationTypeResolutionService';

import { Messages } from '../../../codegen/Localize';
import { WindowLocationService } from '../../../commonInfra/services/WindowLocationService';
import WebHttpClient from '../../../httpClient/WebHttpClient';
import { EmailValidationHelper } from '../../../ts/helpers/EmailValidationHelper';
import { CommonErrorCode } from '../../../ts/helpers/ErrorCodes';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { FileInput, FileInputProps } from '../../../userManagement/components/FileInput';
import { ServerSettingsUnauthenticated } from '../../services/ServerSettingsUnauthenticated';
import {
    ISAMLAuthenticationProvider,
    SiteAuthenticationSettingsService } from '../services/SiteAuthenticationSettingsService';
import {
    samlDisabled,
    samlProviderDarkHighlightTextStyle,
    samlProviderTextDescriptionStyle,
    samlTableStyle,
    samlTableTopPadding} from '../styles/SAMLAuthenticationProviderStyle';


export interface SAMLProviderProps {
    provider: ISAMLAuthenticationProvider;
    updateSamlProvider?: () => void;
    updateEmbeddedOption?: (allow: boolean) => void;
    updateAllowFullName?: (allow: boolean) => void;
    updateFirstNameMapping?: (name: string) => void;
    updateLastNameMapping?: (name: string) => void;
    updateFullNameMapping?: (name: string) => void;
    updateEmailMapping?: (email: string) => void;
}

export interface SAMLProviderState {
    downloadUrl: (evt: React.MouseEvent, url: string) => void;
    downloadLog: (evt: React.MouseEvent) => void;
    isUsable: () => boolean;
    removeSAMLMetadata: () => void;
    samlMetadataFile: File;
    setSAMLAttributeMapping: () => void;
    strings: _.Dictionary<string>;
    test: () => void;
    uploadSAMLMetadata: () => void;
    userAttributes: IUserAttributes;
    isValidUsername: (username: string) => boolean;
    fileInputProps: FileInputProps;
    embeddedOptionRadioButtonIndex: number;
    fullNameRadioButtonIndex: number;
}

interface IUserAttributes extends _.Dictionary<string> {}

const downloadLogEndpoint = 'siteSAMLLog';


export class SAMLAuthenticationProvider extends React.Component<SAMLProviderProps, SAMLProviderState> {
    private messageListener: (evt: MessageEvent) => void;

    constructor(props: SAMLProviderProps) {
        super(props);
        this.state = {
            downloadUrl: (evt: React.MouseEvent<HTMLButtonElement>, url: string) => {
                evt.preventDefault();
                WindowLocationService.download(url);
            },
            downloadLog: (evt: React.MouseEvent<HTMLButtonElement>) => {
                this.state.downloadUrl(evt, WebHttpClient.FILE_UPLOAD_API_PREFIX + downloadLogEndpoint);
            },
            isUsable: () => {
                return !!(this.props.provider.identityProviderEntityId);
            },
            removeSAMLMetadata: () => {
                this.handleUpdate(SiteAuthenticationSettingsService.removeSAMLMetadata(this.props.provider.id))
                  .then((resolved) => {
                    this.props.updateSamlProvider();
                  });
            },
            samlMetadataFile: null,
            setSAMLAttributeMapping: () => {
                this.handleUpdate(SiteAuthenticationSettingsService.setSAMLAttributeMapping(this.props.provider.id, this.props.provider.attributeMapping))
                  .then((resolved) => ToasterService.instance.pop({ type: ToastType.Info, text: Messages.siteAuthenticationAttributesSaved()}));
            },
            strings: this.strings,
            test: () => {
                WindowLocationService.open(this.props.provider.testLoginUrl,
                  'testLogin', { left: 20, top: 20, width: 500, height: 600, toolbar: 1, resizable: 1 });
            },
            uploadSAMLMetadata: () => {
                this.handleUpdate(SiteAuthenticationSettingsService.uploadSAMLMetadata(this.props.provider.id, this.state.samlMetadataFile))
                  .then((resolved) => {
                    this.props.updateSamlProvider();
                    ToasterService.instance.pop({ type: ToastType.Info, text: Messages.siteAuthenticationIdPImportSuccess()});
                  });
            },
            userAttributes: null,
            isValidUsername: ServerSettingsUnauthenticated.isTableauIdSupported() ? EmailValidationHelper.isValidEmail : this.notBlank,
            fileInputProps: {
                fileName: 'entity_descriptor',
                testId:'idp-metadata-file-input',
                handleFileInput: this.handleFileInput
            },
            embeddedOptionRadioButtonIndex: this.props.provider.allowEmbeddedAuthentication ? 1 : 0,
            fullNameRadioButtonIndex: this.props.provider.attributeMapping.useFullName ? 1 : 0
        };

        this.messageListener = (evt: MessageEvent) => this.receiveMessage(evt);
    }

    public componentDidMount() {
        const baseDomain = this.getBaseDomain(WindowLocationService.getOrigin());
        const testLoginBaseDomain = this.getBaseDomain(this.props.provider.testLoginUrl);

        // Enable cross-origin access to this window from the new window, provided they share the same base domain
        if (baseDomain === testLoginBaseDomain) {
            // Test login window is expected to postMessage bearing resolved UserDetails
            window.addEventListener('message', this.messageListener);
            if (!this.isRunningUnderTest()) {
                document.domain = baseDomain;
            }
        }
    }

    public componentWillUnmount() {
        // Unregister message listener on unmount to prevent memory leak
        window.removeEventListener('message', this.messageListener);
    }

    private translateValues = {
        SERVER_LEVEL_AUTH: AuthenticationTypeResolutionService.siteAuthDefaultLabel(),
        SERVER_NAME: ServerSettingsUnauthenticated.serverName()
    }

    private strings = {
        siteAuthenticationType:                 ServerSettingsUnauthenticated.isTableauIdSupported() ? Messages.siteAuthenticationType(): Messages.siteAuthenticationTypeNonTableauIDDesc(),
        defaultSiteAuthenticationType:          ServerSettingsUnauthenticated.isTableauIdSupported() ? Messages.siteAuthenticationTypeTableau(): Messages.siteAuthenticationServerLevelDefaultAuth(this.translateValues),
        siteAuthenticationStep1:                Messages.siteAuthenticationStep1(this.translateValues),
        siteAuthenticationExportFromSpOption2:  Messages.siteAuthenticationExportFromSpOption2(this.translateValues),
        siteAuthenticationEntityId:             Messages.siteAuthenticationEntityId(this.translateValues),
        siteAuthenticationImportToIdP:          Messages.siteAuthenticationImportToIdP(this.translateValues),
        siteAuthenticationExportMetadataFromIdP:Messages.siteAuthenticationExportMetadataFromIdP(this.translateValues),
        siteAuthenticationStep4:                Messages.siteAuthenticationStep4(this.translateValues),
        siteAuthenticationStep5Desc:            Messages.siteAuthenticationStep5Desc(this.translateValues),
        siteAuthenticationTableauAttribute:     Messages.siteAuthenticationTableauAttribute(this.translateValues),
        usernameMapping:                        ServerSettingsUnauthenticated.isTableauIdSupported() ? Messages.email() : Messages.usernameOrEmail(),
        usernameMappingDescription:             ServerSettingsUnauthenticated.isTableauIdSupported() ? Messages.siteAuthenticationEmailsMustMatch(this.translateValues) : Messages.siteAuthenticationUsernameMustMatch(),
        siteAuthenticationNameAssertionsDesc:   Messages.siteAuthenticationNameAssertionsDesc(this.translateValues),
        siteAuthenticationTroubleshootOption1:  Messages.siteAuthenticationTroubleshootOption1(),
        siteAuthenticationTroubleshootOption2:  Messages.siteAuthenticationTroubleshootOption2(this.translateValues),
        siteAuthenticationTroubleshootOption3:  ServerSettingsUnauthenticated.isTableauIdSupported() ? Messages.siteAuthenticationTroubleshootOption3() : Messages.siteAuthenticationTroubleshootOption3NonTableauID()
    }

    private handleFileInput = (file: File) => {
        this.setState({samlMetadataFile: file});
    }

    private notBlank(s: string): boolean {
        return s && /\S+/.test(s);
    }

    private handleUpdate(promise: Promise<any>): Promise<any> {
        promise.catch((reason: number) => {
          let translateKey = Messages.unexpectedError();
          if (reason === CommonErrorCode.InsecureSAMLEndpoint) {
            translateKey = Messages.siteAuthenticationIdPImportFailureInsecureEndpoint();
          } else if (reason === CommonErrorCode.InvalidSAMLIdpMetadata) {
            translateKey = Messages.siteAuthenticationIdPImportFailureInvalidMetadata();
          }
          ToasterService.instance.pop({ type: ToastType.Error, text: translateKey});
        });
        return promise;
    }

    private setUserAttributes(userAttributes: IUserAttributes) {
        this.setState({userAttributes: userAttributes});
    }

    private getHostName(url: string): string {
        // Can't use URL since it's not supported in IE (or node)
        const urlParsingNode = document.createElement('a');
        urlParsingNode.setAttribute('href', url);
        return urlParsingNode.hostname;
    }

    private getBaseDomain(url: string): string {
        const hostName = this.getHostName(url);
        // If the host name is ip address, returns the ip address as the domain.
        if (this.isIPV4(hostName)) {
            return hostName;
        }

        return hostName.split('.').slice(-2).join('.');
    }

    private isIPV4(host: string): boolean {
        const elements = host.split('.');
        if (elements.length === 4) {
            return elements.every((element) => {
                const value = parseInt(element, 10);
                return !isNaN(value) && value >=0 && value <= 255;
            });
        }

        return false;
    }

    private isRunningUnderTest(): boolean {
        return window.hasOwnProperty('mocha');
    }

    public receiveMessage(evt: MessageEvent) {
        if (this.getHostName(evt.origin) !== this.getHostName(this.props.provider.testLoginUrl) && !this.isRunningUnderTest()) {
          return;
        }
        const userAttributes: IUserAttributes = JSON.parse(evt.data);
        this.setUserAttributes(userAttributes);
        if (!this.isRunningUnderTest()) {
          // (TFS 700140) This line causes the Karma debug window to close, when run via the unit tests
          (evt.source as any).close();
        }
    }

    private renderStep1(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className="tb-group-name">
                    <span className="tb-group-number">1</span>
                    {this.strings.siteAuthenticationStep1}
                </div>
                <div className="tb-group-content">
                    <div style={samlProviderTextDescriptionStyle}>
                        {Messages.siteAuthenticationExportFromSpOptions()}
                        <div className="tb-auth-options-list">
                            &nbsp;
                            <div className="tb-auth-options-list-item">
                                &#x25cf;
                                &nbsp;
                                {Messages.siteAuthenticationExportFromSpOption1()}
                                &nbsp;
                                &nbsp;
                                <ButtonWidget
                                buttonType={ButtonType.Outline}
                                testId={'export-metadata-button'}
                                onClick={(evt) => {this.state.downloadUrl(evt, this.props.provider.serviceProviderEntityId);}}>
                                    {Messages.siteAuthenticationExportSpMetadata()}
                                </ButtonWidget>
                            </div>
                            {Messages.or()}
                            <div className="tb-auth-options-list-item">
                                &#x25cf;
                                &nbsp;
                                <span>
                                    {this.strings.siteAuthenticationExportFromSpOption2}
                                </span>
                            </div>
                        </div>
                        <div className="tb-auth-options-row">
                            <div className="tb-auth-options-label">
                                {this.strings.siteAuthenticationEntityId}
                            </div>
                            <TextFieldWidget
                            testId='server-entity-id-input'
                            label={''}
                            value={this.props.provider.serviceProviderEntityId}
                            readOnly={true}
                            size={35}
                            />
                        </div>
                        <div className="tb-auth-options-row">
                            <div className="tb-auth-options-label">
                                {Messages.siteAuthenticationAcsUrl()}
                            </div>
                            <TextFieldWidget
                            testId='assertion-consumer-serviceurl-input'
                            label={''}
                            value={this.props.provider.acsUrl}
                            readOnly={true}
                            size={35}
                            />
                        </div>
                        <div className="tb-auth-options-row">
                            <ButtonWidget
                            className="tb-outline-button.tb-align-right"
                            buttonType={ButtonType.Outline}
                            onClick={(evt) => {this.state.downloadUrl(evt, this.props.provider.certificateUrl);}}
                            testId="download-certificate-button">
                                {Messages.siteAuthenticationDownloadCertificate()}
                            </ButtonWidget>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Import metadata to IDP
    private renderStep2(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className="tb-group-name">
                    <span className="tb-group-number">2</span>
                    <span style={samlProviderDarkHighlightTextStyle}>
                        {Messages.siteAuthenticationExternalStep()}
                        &nbsp;
                    </span>
                    <span>
                        {Messages.siteAuthenticationStep2()}
                    </span>
                </div>
                <div className="tb-group-content">
                    <div style={samlProviderTextDescriptionStyle}>
                        {this.strings.siteAuthenticationImportToIdP}
                    </div>
                </div>
            </div>
        );
    }

    // Export metadata to IDP
    private renderStep3(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className="tb-group-name">
                    <span className="tb-group-number">3</span>
                    <span style={samlProviderDarkHighlightTextStyle}>
                        {Messages.siteAuthenticationExternalStep()}
                        &nbsp;
                    </span>
                    <span>
                        {Messages.siteAuthenticationStep3()}
                    </span>
                </div>
                <div className="tb-group-content">
                    <div style={samlProviderTextDescriptionStyle}>
                        {this.strings.siteAuthenticationExportMetadataFromIdP}
                    </div>
                </div>
            </div>
        );
    }

    // Import metadata to Tableau Online
    private renderStep4(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className="tb-group-name">
                    <span className="tb-group-number">4</span>
                    <span>{this.strings.siteAuthenticationStep4}</span>
                </div>
                <div className="tb-group-content">
                    <div className="tb-auth-options-row">
                        <div className="tb-auth-options-label">
                            <div style={samlProviderTextDescriptionStyle}>
                                {Messages.siteAuthenticationIdPMetadataFile()}
                            </div>
                        </div>
                        <FileInput
                        fileUploadId={this.state.fileInputProps.fileUploadId}
                        fileName={this.state.fileInputProps.fileName}
                        handleFileInput={this.state.fileInputProps.handleFileInput}
                        testId='import-saml-file'/>
                        <ButtonWidget
                        className="tb-align-right"
                        buttonType={ButtonType.Outline}
                        disabled={!this.state.samlMetadataFile}
                        onClick={this.state.uploadSAMLMetadata}
                        testId="idp-apply-metadata-button">
                            {Messages.apply()}
                        </ButtonWidget>
                    </div>
                    <div className="tb-auth-options-row">
                        <div className="tb-auth-options-label">
                            <div style={samlProviderTextDescriptionStyle}>
                                {Messages.siteAuthenticationIdPEntityId()}
                            </div>
                        </div>
                        <TextFieldWidget
                            testId='input-provider-entity-id'
                            label={''}
                            key={this.props.provider.identityProviderEntityId}
                            value={this.props.provider.identityProviderEntityId}
                            readOnly={true}
                            size={35}
                            disabled={!this.state.isUsable()}
                        />
                    </div>
                    <div className="tb-auth-options-row">
                        <div className="tb-auth-options-label">
                            <div style={samlProviderTextDescriptionStyle}>
                                {Messages.siteAuthenticationSSOServiceURL()}
                            </div>
                        </div>
                        <TextFieldWidget
                            testId='input-provider-sso'
                            label={''}
                            key={this.props.provider.ssoServiceURL}
                            value={this.props.provider.ssoServiceURL}
                            readOnly={true}
                            size={35}
                            disabled={!this.state.isUsable()}
                        />
                    </div>
                    <div style={samlProviderTextDescriptionStyle}>
                        {this.state.isUsable() && this.props.provider.isLogoutSupported ? Messages.siteAuthenticationIdPSupportsSingleLogout() : null}
                        {this.state.isUsable() && !this.props.provider.isLogoutSupported ? Messages.siteAuthenticationIdPDoesNotSupportSingleLogout() : null}
                    </div>
                    <div className="tb-auth-options-right-align">
                        <ButtonWidget
                        className="tb-float-left"
                        buttonType={ButtonType.Outline}
                        disabled={!this.state.isUsable()}
                        onClick={this.state.test}
                        testId="idp-test-connection-button">
                            {Messages.siteAuthenticationTestLogin()}
                        </ButtonWidget>
                        <ButtonWidget
                        buttonType={ButtonType.Outline}
                        disabled={!this.state.isUsable()}
                        onClick={this.state.removeSAMLMetadata}
                        testId="idp-remove-button">
                            {Messages.siteAuthenticationRemoveIdentityProvider()}
                        </ButtonWidget>
                    </div>
                    {this.state.userAttributes ?
                        <div className="tb-enabled-color">
                            {Messages.testConnectionSuccess()}
                        </div>
                    : null}
                </div>
            </div>
        );
    }

    // Match assertions
    private renderStep5(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className={this.state.isUsable() ? '' : 'tb-disabled'}>
                    <div style={samlProviderTextDescriptionStyle}>
                        <div className="tb-group-name">
                                <span className="tb-group-number">5</span>
                                <span>{Messages.siteAuthenticationStep5()}</span>
                        </div>
                        <div className="tb-group-content">
                            {this.strings.siteAuthenticationStep5Desc}
                            <div style={samlTableTopPadding}>
                                <div className="tb-auth-assertions">
                                    <span className="tb-auth-table-header">
                                        <span className="tb-auth-table-header-left">
                                            {this.strings.siteAuthenticationTableauAttribute}
                                        </span>
                                        <span className="tb-auth-table-header-right">
                                            {Messages.siteAuthenticationIdPName()}
                                        </span>
                                    </span>
                                    {this.renderStep5Tables()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderStep5Tables(): JSX.Element {
        const allowFullNameButtons = [
            {
                label: 'First and last name',
                testId: 'idp-attribute-usefirstlastname-radiobutton',
                value: false
            },
            {
                label: 'Full name',
                testId: 'idp-attribute-usefullname-radiobutton',
                value: true
            }
        ];
        const handleChange = (allow: boolean) => {
            this.props.updateAllowFullName(allow);
            this.setState({fullNameRadioButtonIndex: allow? 1 : 0});
        };
        let attributesList = this.state.userAttributes && Object.entries(this.state.userAttributes).map(([attrName, attrVal]) => {
                return (
                    <option key={attrName} value={attrName}>{attrName + ' (' + attrVal + ')'}</option>
                );
        }, this);

        return (
            <div style={samlTableStyle}>
                <table>
                    <tbody>
                        <tr>
                            <td colSpan={2}>{this.strings.usernameMapping}</td>
                        </tr>
                        <tr>
                            <td>{this.strings.usernameMappingDescription}</td>
                            <td>
                                {this.state.userAttributes
                                ? <select id='idp-email-mapping-dropdown' onBlur={(evt) => this.props.updateEmailMapping(evt.target.value)}
                                    style={!this.state.isUsable() ? samlDisabled : null}>
                                    {attributesList}
                                </select>
                                : <TextFieldWidget
                                    onBlur={(evt) => this.props.updateEmailMapping(evt.target.value)}
                                    testId='idp-attribute-assertion-textbox'
                                    label={''}
                                    disabled={!this.state.isUsable()}
                                    defaultValue={this.props.provider.attributeMapping.emailMapping}>
                                </TextFieldWidget>}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <table>
                    <tbody>
                        <tr>
                            <td colSpan={3}>{Messages.siteAuthenticationDisplayName()}</td>
                        </tr>
                        <tr>
                            <td colSpan={3}>{this.strings.siteAuthenticationNameAssertionsDesc}</td>
                        </tr>
                        <tr>
                            <td colSpan={3} className="tb-auth-assertions-radio">
                                <div id="full-name-radio-button" style={this.state.isUsable() ? null : samlDisabled}>
                                    <RadioButtonWithLabelGroupWidget
                                        ariaLabel={'allowFullNameRadioButtons'}
                                        className={'tb-dialog-authentication-option'}
                                        testId="allow-fullname-radio-buttons"
                                        buttonList={allowFullNameButtons}
                                        checkedIndex={this.state.fullNameRadioButtonIndex}
                                        handleChange={handleChange}
                                        layout={RadioLayoutType.Horizontal}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr className="tb-auth-assertion-grouping">
                            <td>&nbsp;</td>
                            <td className="tb-auth-assertions-radio-label">
                                {Messages.siteAuthenticationFirstName()}
                            </td>
                            <td>
                                {this.state.userAttributes
                                ? <select id='idp-firstname-mapping-dropdown' onBlur={(evt) => this.props.updateFirstNameMapping(evt.target.value)}
                                    style={!this.state.isUsable() || this.props.provider.attributeMapping.useFullName ? samlDisabled : null}>
                                    {attributesList}
                                </select>
                                : <TextFieldWidget
                                    onBlur={(evt) => this.props.updateFirstNameMapping(evt.target.value)}
                                    testId='idp-attribute-firstname-textbox'
                                    label={''}
                                    disabled={!this.state.isUsable() || this.props.provider.attributeMapping.useFullName}
                                    defaultValue={this.props.provider.attributeMapping.firstNameMapping}>
                                </TextFieldWidget>}
                            </td>
                        </tr>
                        <tr className="tb-auth-assertion-grouping">
                            <td>&nbsp;</td>
                            <td className="tb-auth-assertions-radio-label">
                                {Messages.siteAuthenticationLastName()}
                            </td>
                            <td>
                                {this.state.userAttributes
                                ? <select id='idp-lastname-mapping-dropdown' onBlur={(evt) => this.props.updateLastNameMapping(evt.target.value)}
                                    style={!this.state.isUsable() || this.props.provider.attributeMapping.useFullName ? samlDisabled : null}>
                                    {attributesList}
                                </select>
                                : <TextFieldWidget
                                    onBlur={(evt) => this.props.updateLastNameMapping(evt.target.value)}
                                    testId='idp-attribute-lastname-textbox'
                                    label={''}
                                    disabled={!this.state.isUsable() || this.props.provider.attributeMapping.useFullName}
                                    defaultValue={this.props.provider.attributeMapping.lastNameMapping}>
                                </TextFieldWidget>}
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={3}></td>
                        </tr>
                        <tr className="tb-auth-assertion-grouping">
                            <td className="tb-auth-assertions-radio">
                                &nbsp;
                            </td>
                            <td className="tb-auth-assertions-radio-label">
                                {Messages.siteAuthenticationFullName()}
                            </td>
                            <td>
                                {this.state.userAttributes
                                ? <select id='idp-fullname-mapping-dropdown' onBlur={(evt) => this.props.updateFullNameMapping(evt.target.value)}
                                    style={!this.state.isUsable() || !this.props.provider.attributeMapping.useFullName ? samlDisabled : null}>
                                    {attributesList}
                                </select>
                                : <TextFieldWidget
                                    onBlur={(evt) => this.props.updateFullNameMapping(evt.target.value)}
                                    testId='idp-attribute-fullname-textbox'
                                    label={''}
                                    disabled={!this.state.isUsable() || !this.props.provider.attributeMapping.useFullName}
                                    defaultValue={this.props.provider.attributeMapping.fullNameMapping}>
                                </TextFieldWidget>}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div className="tb-auth-options-right-align">
                    <ButtonWidget
                        buttonType={ButtonType.Outline}
                        disabled={!this.state.isUsable()}
                        onClick={this.state.setSAMLAttributeMapping}
                        testId="idp-apply-attribute-button">
                            {Messages.apply()}
                    </ButtonWidget>
                </div>
            </div>
        );
    }

    // Embedding options
    private renderStep6(): JSX.Element {
        const embeddedOptionsButtons = [
            {
                label: Messages.siteAuthenticationEmbeddingDenied(),
                testId: 'authenticate-embedded-viz-popup-button',
                value: false
            },
            {
                label: Messages.siteAuthenticationEmbeddingAllowed(),
                testId: 'authenticate-embedded-viz-iframe-button',
                value: true
            }
        ];
        const handleChange = (allow: boolean) => {
            this.props.updateEmbeddedOption(allow);
            this.setState({embeddedOptionRadioButtonIndex: allow? 1 : 0});
        };

        return (
            <div className="tb-settings-group">
                <div style={samlProviderTextDescriptionStyle}>
                    <div className={this.state.isUsable() ? '' : 'tb-disabled'}>
                        <div className="tb-group-name">
                            <span className="tb-group-number">6</span>
                            <span>{Messages.ssoAuthEmbeddingOptions()}</span>
                        </div>
                        <div className="tb-group-content">
                            {Messages.ssoAuthEmbeddingOptionsDesc()}
                        </div>
                        <div className="tb-group-content">
                            <div style={this.state.isUsable() ? null : samlDisabled}>
                                <RadioButtonWithLabelGroupWidget
                                    ariaLabel={'embeddedOptionRadioButtons'}
                                    className={'tb-dialog-authentication-option'}
                                    testId="embedded-option-radio-buttons"
                                    buttonList={embeddedOptionsButtons}
                                    checkedIndex={this.state.embeddedOptionRadioButtonIndex}
                                    handleChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Troubleshooting
    private renderStep7(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className="tb-group-name">
                    <span className="tb-group-number">7</span>
                    <span>{Messages.siteAuthenticationStep7()}</span>
                </div>
                <div className="tb-group-content">
                    <div style={samlProviderTextDescriptionStyle}>
                        {Messages.siteAuthenticationTroubleshootActions()}
                        <div className="tb-auth-options-list">
                            <div className="tb-auth-options-list-item">
                                &#x25cf;
                                &nbsp;
                                {Messages.siteAuthenticationTroubleshootAssertionMatching()}
                                &nbsp;
                                <ButtonWidget
                                buttonType={ButtonType.Outline}
                                onClick={(evt) => this.state.downloadLog(evt)}
                                testId="download-troubleshooting-log-button">
                                    {Messages.siteAuthenticationDownloadLog()}
                                </ButtonWidget>
                            </div>
                            <div className="tb-auth-options-list-item">
                                &#x25cf;
                                &nbsp;
                                <span>{this.strings.siteAuthenticationTroubleshootOption1}</span>
                            </div>
                            <div className="tb-auth-options-list-item">
                                &#x25cf;
                                &nbsp;
                                <span>{this.strings.siteAuthenticationTroubleshootOption2}</span>
                            </div>
                        </div>
                    </div>
                    <span style={samlProviderTextDescriptionStyle}>{this.strings.siteAuthenticationTroubleshootOption3}</span>
                </div>
            </div>
        );
    }

    public render(): JSX.Element {
        return (
            <div className="tb-group-name">
                &nbsp;
                <div className="tb-settings-group">
                    {Messages.siteAuthenticationTypeSAMLDescription2()}
                </div>
                {this.renderStep1()}
                {this.renderStep2()}
                {this.renderStep3()}
                {this.renderStep4()}
                {this.renderStep5()}
                {this.renderStep6()}
                {this.renderStep7()}
            </div>
        );
    }
}
